import { KEYBOARD_LAYOUTS } from "./keyCodeMapper";
import { ES87_F13_LAYOUT_KEYCODE } from "./keyCodeMapperBeiying";

/** ES87 VID/PID（与 supportedDevices 一致） */
export const XINGSHAN_ES87_PRODUCT_ID = 0x1c3e;
export const XINGSHAN_X87_PRODUCT_ID = 0x1c0a; // 7178 decimal
export const XINGSHAN_VENDOR_ID = 0x1ca6;

const ES87_LIGHT_MATRIX = KEYBOARD_LAYOUTS.es87 as number[][];

export function isXingshanEs87Device(
  productId?: number,
  vendorId?: number
): boolean {
  return (
    Number(productId) === XINGSHAN_ES87_PRODUCT_ID &&
    Number(vendorId) === XINGSHAN_VENDOR_ID
  );
}

/** ES87 F13 自定义灯下发 id：固件认 layout HID 104，勿用键帽 id 114 */
export function resolveEs87F13CustomLightWriteId(
  layoutKeycode: number
): number | undefined {
  if (layoutKeycode === ES87_F13_LAYOUT_KEYCODE) {
    return ES87_F13_LAYOUT_KEYCODE;
  }
  return undefined;
}

/**
 * 虚拟键盘选中键 → 自定义灯 6×21 矩阵格（与固件/KEYBOARD_LAYOUTS.es87 列序一致）
 */
export function resolveEs87CustomLightGridCell(
  layoutRow: number,
  keyCode: number,
  fallbackCol: number
): { row: number; col: number } {
  const matrixRow = ES87_LIGHT_MATRIX[layoutRow];
  if (!matrixRow?.length) {
    return { row: layoutRow, col: fallbackCol };
  }
  /** 分裂空格：多颗 keyCode 44 须按 originalCol 对应灯珠列，不能 indexOf 总落在 col 6 */
  if (keyCode === 44 && fallbackCol >= 0 && fallbackCol < matrixRow.length) {
    const atCol = matrixRow[fallbackCol];
    if (atCol === 0 || atCol === 44) {
      return { row: layoutRow, col: fallbackCol };
    }
  }
  const idx = matrixRow.indexOf(keyCode);
  if (idx >= 0) {
    return { row: layoutRow, col: idx };
  }
  return { row: layoutRow, col: fallbackCol };
}

/** 将 UI 选中的 layout 键位列表转为灯光矩阵行列 */
export function mapLayoutKeysToEs87CustomLightCells(
  keys: Array<{
    layoutRow: number;
    keyCode: number;
    originalCol: number;
  }>
): Array<{ row: number; col: number }> {
  return keys.map(({ layoutRow, keyCode, originalCol }) =>
    resolveEs87CustomLightGridCell(layoutRow, keyCode, originalCol)
  );
}

/** 星闪 Alpha 87 灯带：固件 SDK 共 10 颗（Decorate1~3 分段），虚拟灯带按回读数量展示 */
export const XINGSHAN_ES87_STRIP_SDK_COLS = 10;

/** Alpha 87 灯带分段：须分别 get/set 各 Decorate 区域后才有完整效果 */
export const XINGSHAN_ES87_STRIP_SEGMENTS = [
  { area: "Decorate1", cols: 3 },
  { area: "Decorate2", cols: 3 },
  { area: "Decorate3", cols: 4 },
] as const;

export type XingshanEs87StripSegmentArea =
  (typeof XINGSHAN_ES87_STRIP_SEGMENTS)[number]["area"];

export function isXingshanEs87KeyboardEquipment(
  equipment?: { solutionRovider?: string; keyboardType?: string } | null
): boolean {
  return (
    String(equipment?.solutionRovider ?? "") === "xingshan" &&
    String(equipment?.keyboardType ?? "")
      .trim()
      .toLowerCase() === "es87"
  );
}

/** ES68 装饰灯1：公版 SDK 虚拟灯带 26 颗，getDecorate1Custom 实时回显 */
export const XINGSHAN_ES68_STRIP_COLS = 26;
/** X87 HE 装饰灯1：SDK 回读默认 8 颗，按固件实际颗数展示 */
export const XINGSHAN_X87HE_STRIP_DEFAULT_COLS = 8;
/** DK68 Star 装饰灯1：公版 SDK 虚拟灯带 34 颗，getDecorate1Custom 实时回显 */
export const XINGSHAN_DK68STAR_STRIP_COLS = 34;
/** DK68 V2 装饰灯1：公版 SDK 虚拟灯带 26 颗，getDecorate1Custom 实时回显 */
export const XINGSHAN_DK68V2_STRIP_COLS = 26;
/** ES68 虚拟灯带单颗尺寸：宽 50px × 高 10px（与原先 8 颗 × 400px 宽条一致） */
export const XINGSHAN_ES68_STRIP_LED_WIDTH = 50;
export const XINGSHAN_ES68_STRIP_LED_HEIGHT = 10;
/** @deprecated 使用 XINGSHAN_ES68_STRIP_LED_WIDTH */
export const XINGSHAN_ES68_STRIP_LED_SIZE = XINGSHAN_ES68_STRIP_LED_WIDTH;

export function isXingshanEs68KeyboardEquipment(
  equipment?: {
    solutionRovider?: string;
    keyboardType?: string;
    displayname?: string;
  } | null
): boolean {
  return (
    String(equipment?.solutionRovider ?? "") === "xingshan" &&
    String(equipment?.keyboardType ?? "").trim() === "68" &&
    String(equipment?.displayname ?? "").trim() === "ES68"
  );
}

/** ES68 / Lite / EVO / EVO Full：与公版 ES68 相同 26 颗装饰灯带 */
function isXingshanEs68FamilyStripKeyboardEquipment(
  equipment?: {
    solutionRovider?: string;
    keyboardType?: string;
    displayname?: string;
    productName?: string;
  } | null
): boolean {
  if (String(equipment?.solutionRovider ?? "") !== "xingshan") return false;
  if (isXingshanEs68KeyboardEquipment(equipment)) return true;

  const displayname = String(equipment?.displayname ?? "").trim();
  const keyboardType = String(equipment?.keyboardType ?? "").trim();
  if (keyboardType === "68EVO") return true;
  if (displayname === "ES68 Lite") return true;
  if (displayname === "ES68 EVO" || displayname === "ES68 EVO (Full)") {
    return true;
  }

  const blob = `${displayname} ${equipment?.productName ?? ""}`;
  if (/\bES68\s*Lite\b/i.test(blob)) return true;
  if (/\bES68\s*EVO\b/i.test(blob)) return true;
  return false;
}

export function isXingshanX87HeKeyboardEquipment(
  equipment?: {
    solutionRovider?: string;
    keyboardType?: string;
    displayname?: string;
  } | null
): boolean {
  return (
    String(equipment?.solutionRovider ?? "") === "xingshan" &&
    String(equipment?.keyboardType ?? "").trim() === "87" &&
    String(equipment?.displayname ?? "").trim() === "X87 HE"
  );
}

/** ES68 系列 / Gamma75 / X75 Ultra：26 颗公版灯带（SDK/UI 同一路径） */
export function isXingshanEs68StripKeyboardEquipment(
  equipment?: {
    solutionRovider?: string;
    keyboardType?: string;
    displayname?: string;
    productName?: string;
  } | null
): boolean {
  if (isXingshanEs68FamilyStripKeyboardEquipment(equipment)) return true;
  if (String(equipment?.solutionRovider ?? "") !== "xingshan") return false;

  const blob = `${equipment?.displayname ?? ""} ${equipment?.productName ?? ""}`;
  if (/\bX75\s*Ultra\b/i.test(blob) || /\bX75\s*ULTRA\b/i.test(blob)) {
    return true;
  }
  const kt = String(equipment?.keyboardType ?? "").trim();
  if (kt === "gamma75") return true;
  if (/\bx75\s*ultra\b/i.test(blob)) return false;
  return /\bEXXGAMMA75\b/i.test(blob) || /\bgamma\s*75\b/i.test(blob);
}

/** X87 HE：灯带按固件回读颗数展示（默认 8 颗） */
export function isXingshanHeLikeStripKeyboardEquipment(
  equipment?: {
    solutionRovider?: string;
    keyboardType?: string;
    displayname?: string;
    productName?: string;
  } | null
): boolean {
  return isXingshanX87HeKeyboardEquipment(equipment);
}

export function isXingshanDk68StarKeyboardEquipment(
  equipment?: {
    solutionRovider?: string;
    keyboardType?: string;
    displayname?: string;
  } | null
): boolean {
  return (
    String(equipment?.solutionRovider ?? "") === "xingshan" &&
    String(equipment?.keyboardType ?? "").trim() === "68" &&
    String(equipment?.displayname ?? "").trim() === "DK68 Star"
  );
}

export function isXingshanDk68V2KeyboardEquipment(
  equipment?: {
    solutionRovider?: string;
    keyboardType?: string;
    displayname?: string;
  } | null
): boolean {
  return (
    String(equipment?.solutionRovider ?? "") === "xingshan" &&
    String(equipment?.keyboardType ?? "").trim() === "68" &&
    String(equipment?.displayname ?? "").trim() === "DK68 V2"
  );
}

/** DK68 Star（34 颗）/ DK68 V2（26 颗）：getDecorate1Custom 实时回显虚拟灯带 */
export function isXingshanDk68RealtimeStripKeyboardEquipment(
  equipment?: {
    solutionRovider?: string;
    keyboardType?: string;
    displayname?: string;
  } | null
): boolean {
  return (
    isXingshanDk68StarKeyboardEquipment(equipment) ||
    isXingshanDk68V2KeyboardEquipment(equipment)
  );
}

/** DK68 / DK68 Star / DK68 V2 / DK63 Star：灯带 mode 0–3 仅改展示文案，下发 mode 值不变 */
const XINGSHAN_DK68_FAMILY_STRIP_DISPLAYNAMES = new Set([
  "DK68",
  "DK68 Star",
  "DK68 V2",
  "DK63 Star",
]);

export function isXingshanDk68FamilyStripKeyboardEquipment(
  equipment?: {
    solutionRovider?: string;
    displayname?: string;
  } | null
): boolean {
  return (
    String(equipment?.solutionRovider ?? "") === "xingshan" &&
    XINGSHAN_DK68_FAMILY_STRIP_DISPLAYNAMES.has(
      String(equipment?.displayname ?? "").trim()
    )
  );
}

export const XINGSHAN_DK68_FAMILY_STRIP_MODE_LABEL_KEYS = [
  "keyboard.lighting.modes.rainbow",
  "keyboard.lighting.modes.glow",
  "keyboard.lighting.modes.tide",
  "keyboard.lighting.modes.static",
] as const;

export const XINGSHAN_DEFAULT_STRIP_MODE_LABEL_KEYS = [
  "keyboard.lighting.modes.static",
  "keyboard.lighting.modes.wave",
  "keyboard.lighting.modes.sparkle",
  "keyboard.lighting.modes.flowExtend",
] as const;

/** ES68 系列 / X87 HE / DK68 Star / DK68 V2 / Gamma75 / X75 Ultra：公版 getDecorate1Custom 实时回显虚拟灯带 */
export function isXingshanRealtimeStripKeyboardEquipment(
  equipment?: {
    solutionRovider?: string;
    keyboardType?: string;
    displayname?: string;
    productName?: string;
  } | null
): boolean {
  return (
    isXingshanEs68StripKeyboardEquipment(equipment) ||
    isXingshanHeLikeStripKeyboardEquipment(equipment) ||
    isXingshanDk68RealtimeStripKeyboardEquipment(equipment)
  );
}

export type XingshanDecorativeStripEquipment = {
  solutionRovider?: string;
  keyboardType?: string;
  displayname?: string;
  productName?: string;
};

/** 装饰灯轮询请求列数（初始 default cols） */
export function getXingshanDecorativeStripDefaultCols(
  equipment?: XingshanDecorativeStripEquipment | null,
  options?: { isXingshanEs87?: boolean }
): number {
  if (options?.isXingshanEs87) {
    return XINGSHAN_ES87_STRIP_SEGMENTS[0].cols;
  }
  if (isXingshanEs68StripKeyboardEquipment(equipment)) {
    return XINGSHAN_ES68_STRIP_COLS;
  }
  if (isXingshanDk68V2KeyboardEquipment(equipment)) {
    return XINGSHAN_DK68V2_STRIP_COLS;
  }
  if (isXingshanDk68StarKeyboardEquipment(equipment)) {
    return XINGSHAN_DK68STAR_STRIP_COLS;
  }
  if (isXingshanHeLikeStripKeyboardEquipment(equipment)) {
    return XINGSHAN_X87HE_STRIP_DEFAULT_COLS;
  }
  return 8;
}

/** 虚拟灯带展示列数 */
export function getXingshanDecorativeStripCols(
  equipment?: XingshanDecorativeStripEquipment | null,
  decorateRow?: { length: number } | null
): number {
  if (isXingshanEs68StripKeyboardEquipment(equipment)) {
    return XINGSHAN_ES68_STRIP_COLS;
  }
  if (isXingshanDk68V2KeyboardEquipment(equipment)) {
    return XINGSHAN_DK68V2_STRIP_COLS;
  }
  if (isXingshanDk68StarKeyboardEquipment(equipment)) {
    return XINGSHAN_DK68STAR_STRIP_COLS;
  }
  const rowLen = decorateRow?.length ?? 0;
  if (isXingshanHeLikeStripKeyboardEquipment(equipment)) {
    return rowLen > 0 ? rowLen : XINGSHAN_X87HE_STRIP_DEFAULT_COLS;
  }
  if (rowLen > 0) {
    return rowLen;
  }
  return getXingshanDecorativeStripDefaultCols(equipment);
}

export function isXingshanDecorateStripArea(area?: string): boolean {
  return /^Decorate\d+$/i.test(String(area ?? "").trim());
}

let es87StripSdkRowCache: Es87StripRgbCell[] | null = null;

export function readEs87StripSdkRowCache(): Es87StripRgbCell[] | null {
  return es87StripSdkRowCache;
}

export function writeEs87StripSdkRowCache(row: Es87StripRgbCell[]): void {
  es87StripSdkRowCache = [...row];
}

export function clearEs87StripSdkRowCache(): void {
  es87StripSdkRowCache = null;
}

export type Es87StripRgbCell = {
  R: number;
  G: number;
  B: number;
  isCustom: boolean;
};

function blankEs87StripCell(): Es87StripRgbCell {
  return { R: 0, G: 0, B: 0, isCustom: false };
}

function normalizeEs87StripCell(raw: unknown): Es87StripRgbCell {
  if (!raw || typeof raw !== "object") return blankEs87StripCell();
  const cell = raw as Record<string, unknown>;
  const clamp = (value: unknown) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(255, Math.round(n)));
  };
  return {
    R: clamp(cell.R ?? cell.r ?? cell.red),
    G: clamp(cell.G ?? cell.g ?? cell.green),
    B: clamp(cell.B ?? cell.b ?? cell.blue),
    isCustom: Boolean(cell.isCustom),
  };
}

function parseXingshanStripSdkCells(raw: unknown): Es87StripRgbCell[] {
  const cells: Es87StripRgbCell[] = [];
  if (!Array.isArray(raw)) return cells;
  if (raw.length > 0 && Array.isArray(raw[0])) {
    for (const row of raw) {
      if (!Array.isArray(row)) continue;
      for (const cell of row) cells.push(normalizeEs87StripCell(cell));
    }
    return cells;
  }
  for (const cell of raw) cells.push(normalizeEs87StripCell(cell));
  return cells;
}

/** 解析 getDecorate1Custom 回包为 rows×cols 点阵网格（勿 flatten 成灯带单行） */
export function normalizeXingshanDotMatrixSdkGrid(
  raw: unknown,
  rows: number,
  cols: number
): Es87StripRgbCell[][] {
  const safeRows = Math.max(1, Math.floor(Number(rows) || 1));
  const safeCols = Math.max(1, Math.floor(Number(cols) || 1));
  const grid: Es87StripRgbCell[][] = Array.from({ length: safeRows }, () =>
    Array.from({ length: safeCols }, () => blankEs87StripCell())
  );
  if (!Array.isArray(raw)) return grid;

  if (raw.length > 0 && Array.isArray(raw[0])) {
    for (let row = 0; row < Math.min(safeRows, raw.length); row += 1) {
      const sourceRow = raw[row];
      if (!Array.isArray(sourceRow)) continue;
      for (let col = 0; col < Math.min(safeCols, sourceRow.length); col += 1) {
        grid[row][col] = normalizeEs87StripCell(sourceRow[col]);
      }
    }
    return grid;
  }

  const flatCells = parseXingshanStripSdkCells(raw);
  for (
    let index = 0;
    index < Math.min(safeRows * safeCols, flatCells.length);
    index += 1
  ) {
    const row = Math.floor(index / safeCols);
    const col = index % safeCols;
    grid[row][col] = flatCells[index];
  }
  return grid;
}

/** 解析 getDecorate1Custom 回包为单行灯珠数组 */
export function normalizeXingshanEs87StripSdkRow(
  raw: unknown,
  sdkCols = XINGSHAN_ES87_STRIP_SDK_COLS
): Es87StripRgbCell[] {
  const cells = parseXingshanStripSdkCells(raw);
  const row = Array.from(
    { length: sdkCols },
    (_, i) => cells[i] ?? blankEs87StripCell()
  );
  return row;
}

/** ES68 / X87 HE 灯带：固定 minCols 颗，超出截断、不足补黑 */
export function normalizeXingshanEs68StripSdkRow(
  raw: unknown,
  minCols = XINGSHAN_ES68_STRIP_COLS
): Es87StripRgbCell[] {
  const cells = parseXingshanStripSdkCells(raw);
  return Array.from(
    { length: minCols },
    (_, i) => cells[i] ?? blankEs87StripCell()
  );
}

/** X87 HE 灯带：按固件回读颗数展示，无数据时用 fallbackCols */
export function normalizeXingshanX87HeStripSdkRow(
  raw: unknown,
  fallbackCols = XINGSHAN_X87HE_STRIP_DEFAULT_COLS
): Es87StripRgbCell[] {
  const cells = parseXingshanStripSdkCells(raw);
  const rowLength = cells.length > 0 ? cells.length : fallbackCols;
  return Array.from(
    { length: rowLength },
    (_, i) => cells[i] ?? blankEs87StripCell()
  );
}

/** SDK 回读行 → UI 展示行（默认展示全部回读灯珠） */
export function mapXingshanEs87StripSdkRowToUi(
  sdkRow: Es87StripRgbCell[],
  uiCols?: number
): Es87StripRgbCell[] {
  const targetCols = uiCols ?? sdkRow.length;
  if (targetCols <= 0) return [...sdkRow];
  const row = sdkRow.slice(0, targetCols);
  while (row.length < targetCols) {
    row.push(blankEs87StripCell());
  }
  return row;
}

/** 单段是否存在有效颜色（非全黑） */
export function es87StripSegmentHasColor(row: Es87StripRgbCell[]): boolean {
  return row.some((cell) => cell.R > 0 || cell.G > 0 || cell.B > 0);
}

/** 合并多段 Decorate 回包为单行 SDK 灯珠数组（长度 = 各段实际回读之和） */
export function mergeXingshanEs87StripSegmentRows(
  segmentRows: Es87StripRgbCell[][]
): Es87StripRgbCell[] {
  const merged: Es87StripRgbCell[] = [];
  for (const segment of segmentRows) {
    merged.push(...segment);
  }
  return merged;
}

/** 仅合并有颜色的分段（全黑分段跳过，不参与虚拟灯带回显） */
export function mergeXingshanEs87StripColoredSegmentRows(
  segmentRows: Es87StripRgbCell[][]
): Es87StripRgbCell[] {
  return mergeXingshanEs87StripSegmentRows(
    segmentRows.filter((row) => es87StripSegmentHasColor(row))
  );
}

/** 解析单段 Decorate 回包，保留该段每颗灯珠颜色 */
export function extractEs87StripSegmentColorRow(
  raw: unknown,
  segmentCols: number
): Es87StripRgbCell[] {
  return normalizeXingshanEs87StripSdkRow(raw, segmentCols);
}

let lastEs87StripDebugLogAt = 0;
const ES87_STRIP_DEBUG_LOG_INTERVAL_MS = 2000;

/**
 * 星闪 Alpha 87 虚拟灯带回读：
 * 依次 get Decorate1 → Decorate2 → Decorate3，仅合并有颜色的分段。
 */
export async function readXingshanEs87StripCustomRow(
  fetchSegment: (params: {
    rows: number;
    cols: number;
    area: string;
  }) => Promise<unknown>
): Promise<Es87StripRgbCell[]> {
  const segmentRows: Es87StripRgbCell[][] = [];
  const debugSegments: Array<{
    area: string;
    request: { rows: number; cols: number; area: string };
    raw: unknown;
    parsed: Es87StripRgbCell[];
    included: boolean;
  }> = [];

  for (const seg of XINGSHAN_ES87_STRIP_SEGMENTS) {
    const request = {
      rows: 1,
      cols: seg.cols,
      area: seg.area,
    };
    const raw = await fetchSegment(request);
    const parsed = extractEs87StripSegmentColorRow(raw, seg.cols);
    const included = es87StripSegmentHasColor(parsed);
    if (included) {
      segmentRows.push(parsed);
    }
    debugSegments.push({ area: seg.area, request, raw, parsed, included });
  }

  const merged = mergeXingshanEs87StripSegmentRows(segmentRows);

  const now = Date.now();
  if (now - lastEs87StripDebugLogAt >= ES87_STRIP_DEBUG_LOG_INTERVAL_MS) {
    lastEs87StripDebugLogAt = now;
    console.group("[xingshan-es87-strip] getDecorate1Custom 分段回读");
    for (const item of debugSegments) {
      console.log(`[${item.area}] 请求参数:`, item.request);
      console.log(`[${item.area}] SDK 原始回包:`, item.raw);
      console.log(`[${item.area}] 解析后颜色:`, item.parsed);
      console.log(
        `[${item.area}] 是否参与回显:`,
        item.included ? "是" : "否（全黑跳过）"
      );
    }
    console.log("[xingshan-es87-strip] 合并后虚拟灯带:", merged);
    console.groupEnd();
  }

  return merged;
}

/** 单行 SDK 灯珠数组 → 各 Decorate 分段 */
export function splitEs87StripSdkRowToSegments(
  sdkRow: Es87StripRgbCell[]
): Es87StripRgbCell[][] {
  const rows: Es87StripRgbCell[][] = [];
  let offset = 0;
  for (const seg of XINGSHAN_ES87_STRIP_SEGMENTS) {
    rows.push(
      Array.from(
        { length: seg.cols },
        (_, i) => sdkRow[offset + i] ?? blankEs87StripCell()
      )
    );
    offset += seg.cols;
  }
  return rows;
}

/** UI 灯珠写入完整 SDK 行（长度以 UI 回读/缓存为准） */
export function mergeUiRowIntoEs87StripSdkRow(
  uiRow: Es87StripRgbCell[],
  cachedSdkRow: Es87StripRgbCell[] | null
): Es87StripRgbCell[] {
  const targetLen =
    uiRow.length || cachedSdkRow?.length || XINGSHAN_ES87_STRIP_SDK_COLS;
  const base = cachedSdkRow?.length
    ? [...cachedSdkRow]
    : Array.from({ length: targetLen }, () => blankEs87StripCell());
  const normalizedUi = mapXingshanEs87StripSdkRowToUi(uiRow, targetLen);
  for (let i = 0; i < normalizedUi.length && i < base.length; i++) {
    base[i] = normalizedUi[i];
  }
  while (base.length < targetLen) {
    base.push(blankEs87StripCell());
  }
  return base.slice(0, targetLen);
}
